import React, { FC, ReactNode, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { TimeRangePicker } from "./TimeRangePicker.tsx";
import { ApplyOnMultipleDaysDialog, SelectedDays } from "./ApplyOnMultipleDaysDialog.tsx";
import { format } from "date-fns";
import TranslationWrapper from "../../old/beheer/components/TranslationWrapper/TranslationWrapper.tsx";
import { Day, days, isOfTypeDay, TimeRange, TimeRangeMethods, toTimeRange } from "@jamezz/react-components";
import _ from "lodash";
import { OpeningHoursForDay2 } from "./OpeningHoursSettings.tsx";
import { v7 } from "uuid";
import { CopyExceptionDialog } from "./CopyExceptionDialog.tsx";
import { AddEditExceptionDialog } from "./AddEditExceptionDialog.tsx";

type TimeTableProps<T> = {
  openingHours: {
    [key: string]: OpeningHoursForDay2<T>[] | undefined;
  };
  onChange: (openingHoursDays: { [key: string]: OpeningHoursForDay2<T>[] | undefined }) => void;
  ActionMenu?: FC<TimeTableActionMenuProps<T>>;
  ExtensionComponent?: FC<TimeTableExtensionProps<T>>;
  emptyExtensionData: T;
  timeTableType: "days" | "exceptions";
  closedLabel?: ReactNode;
  open24Label?: ReactNode;
};

export type TimeTableActionMenuProps<T> = {
  defaultData: T;
  openingHours: {
    [key: string]: OpeningHoursForDay2<T>[] | undefined;
  };
  onChange: (openingHoursDays: { [key: string]: OpeningHoursForDay2<T>[] | undefined }) => void;
  row: TimeTableRow<T>;
  onCopy: () => void;
  onRemove: () => void;
  onEdit: () => void;
};

export type TimeTableExtensionProps<T> = {
  onChange: (data: OpeningHoursForDay2<T>) => void;
  openingHours: OpeningHoursForDay2<T>;
  data: T;
  defaultValue: T;
};

export type TimeTableRow<T> = {
  dayOrDate: string | Day;
  timeRanges: {
    id: string;
    hours: string;
    data: T;
  }[];
};

export const TimeTable = <T,>({
  openingHours = {},
  onChange,
  ActionMenu,
  ExtensionComponent,
  emptyExtensionData,
  timeTableType,
  closedLabel,
  open24Label,
}: TimeTableProps<T>) => {
  const [rowToCopy, setRowToCopy] = useState<TimeTableRow<T>>();
  const [isEditRowDialogOpen, setIsEditRowDialogOpen] = useState(false);
  const [rowToEdit, setRowToEdit] = useState<TimeTableRow<T>>();

  const rows: TimeTableRow<T>[] = useMemo(() => {
    return [
      ...(timeTableType === "days"
        ? days.map((day: Day) => {
            return { dayOrDate: day, timeRanges: openingHours[day] ?? [] };
          })
        : []),
      ..._.chain(openingHours)
        .pickBy((value, key) => !(days as string[]).includes(key))
        .map((value, key) => ({ dayOrDate: key, timeRanges: value ?? [] }))
        .value(),
    ];
  }, [timeTableType, openingHours]);

  const handleChange = (newTimeRange: TimeRange | null, dayOrDate: string | Day, defaultData: T) => {
    const newOpeningHours = _.cloneDeep(openingHours);
    if (newTimeRange === null) {
      newOpeningHours[dayOrDate] = [];
    } else {
      newOpeningHours[dayOrDate] = [
        { hours: `${newTimeRange.start}-${newTimeRange.end}`, id: v7(), data: defaultData },
      ];
    }
    onChange(newOpeningHours);
  };

  const handleCopyOpeningHours = (selectedDays: SelectedDays | string[], timeRanges: OpeningHoursForDay2<T>[]) => {
    const newOpeningHours = _.cloneDeep(openingHours);
    Object.entries(selectedDays).map(([day, value]) => {
      if (value && isOfTypeDay(day)) {
        newOpeningHours[day] = timeRanges;
      } else if (value) {
        newOpeningHours[value] = timeRanges;
      }
    });

    onChange(newOpeningHours);
    setRowToCopy(undefined);
  };

  return (
    <Box
      sx={{
        fontSize: "16px",
        color: grey[700],
      }}
    >
      <TableContainer>
        <Table
          sx={{
            [`& .${tableCellClasses.root}`]: {
              borderBottom: "1px solid lightgrey",
              padding: {
                xs: "6px 2px",
                sm: "6px 2px",
              },
            },
            width: "unset",
            minWidth: 320,
          }}
          size={"small"}
        >
          <TableBody sx={{ fontSize: "16px", color: grey[700] }}>
            {rows.map((row) => (
              <TableRow key={row.dayOrDate}>
                <TableCell align={"left"}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    sx={{ justifyContent: "space-between", alignItems: "baseline" }}
                  >
                    <Typography
                      variant={"body1"}
                      onClick={() => {
                        setRowToEdit(row);
                        setIsEditRowDialogOpen(true);
                      }}
                      sx={{ cursor: isOfTypeDay(row.dayOrDate) ? "auto" : "pointer" }}
                    >
                      {isOfTypeDay(row.dayOrDate)
                        ? row.dayOrDate.charAt(0).toUpperCase() + row.dayOrDate.slice(1)
                        : row.dayOrDate.split("-").length > 2
                          ? format(new Date(row.dayOrDate), "dd-MM-yyyy")
                          : format(new Date(row.dayOrDate), "dd-MM")}
                    </Typography>
                    <Switch
                      checked={row.timeRanges.length > 0}
                      onChange={() => {
                        if (row.timeRanges.length > 0) {
                          handleChange(null, row.dayOrDate, emptyExtensionData);
                        } else {
                          handleChange({ start: "00:00", end: "24:00" }, row.dayOrDate, emptyExtensionData);
                        }
                      }}
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction={"column"} gap={1}>
                    {row.timeRanges.length > 0 ? (
                      row.timeRanges.length === 1 &&
                      TimeRangeMethods.timeRangesAreEqual(toTimeRange(row.timeRanges[0].hours), {
                        start: { hours: 0, minutes: 0 },
                        end: { hours: 24, minutes: 0 },
                      }) ? (
                        <Stack direction={"row"} gap={1} alignItems={"center"} flexWrap={"wrap"}>
                          <Typography
                            onClick={() => {
                              handleChange({ start: "00:00", end: "12:00" }, row.dayOrDate, emptyExtensionData);
                            }}
                            sx={{ cursor: "pointer" }}
                          >
                            {open24Label ?? "24H Open"}
                          </Typography>
                          {ExtensionComponent !== undefined && emptyExtensionData !== undefined ? (
                            <ExtensionComponent
                              openingHours={row.timeRanges[0]}
                              data={row.timeRanges[0].data}
                              defaultValue={emptyExtensionData}
                              onChange={(data) => {
                                const newTimes = [...(openingHours[row.dayOrDate] ?? [])];
                                newTimes[0] = data;
                                onChange({ ...openingHours, [row.dayOrDate]: newTimes });
                              }}
                            />
                          ) : undefined}
                        </Stack>
                      ) : (
                        row.timeRanges
                          .sort((a, b) => a.hours.localeCompare(b.hours))
                          .map((openingHoursForDay, index: number) => {
                            const timeRange = toTimeRange(openingHoursForDay.hours);
                            return (
                              <TimeRangePicker
                                key={openingHoursForDay.id}
                                hideDeleteButton={row.timeRanges.length <= 1}
                                timeRange={timeRange}
                                onChange={(newTimeRange) => {
                                  const newTimes = [...(openingHours[row.dayOrDate] ?? [])];
                                  if (newTimeRange) {
                                    newTimes[index] = {
                                      ...newTimes[index],
                                      hours: TimeRangeMethods.timeRangeToString(newTimeRange),
                                    };
                                  } else {
                                    newTimes.splice(index, 1);
                                  }
                                  const newDays = { ...openingHours, [row.dayOrDate]: newTimes };

                                  onChange(newDays);
                                }}
                                ExtensionComponent={
                                  ExtensionComponent !== undefined && emptyExtensionData !== undefined ? (
                                    <ExtensionComponent
                                      openingHours={openingHoursForDay}
                                      data={openingHoursForDay.data}
                                      defaultValue={emptyExtensionData}
                                      onChange={(data) => {
                                        const newTimes = [...(openingHours[row.dayOrDate] ?? [])];
                                        newTimes[index] = data;
                                        onChange({ ...openingHours, [row.dayOrDate]: newTimes });
                                      }}
                                    />
                                  ) : undefined
                                }
                              />
                            );
                          })
                      )
                    ) : (
                      <Typography
                        variant={"body1"}
                        onClick={() => {
                          handleChange({ start: "00:00", end: "12:00" }, row.dayOrDate, emptyExtensionData);
                        }}
                        sx={{ cursor: "pointer" }}
                      >
                        {closedLabel ?? <TranslationWrapper tKey={"timeoddsmodal.closed"} tDefault={"Closed"} />}
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                {ActionMenu ? (
                  <TableCell>
                    <ActionMenu
                      defaultData={emptyExtensionData}
                      row={row}
                      openingHours={openingHours}
                      onChange={(newOpeningHours) => {
                        onChange(newOpeningHours);
                      }}
                      onCopy={() => {
                        setRowToCopy(row);
                      }}
                      onRemove={() => {
                        const newOpeningHours = _.cloneDeep(openingHours);
                        delete newOpeningHours[row.dayOrDate];
                        onChange(newOpeningHours);
                      }}
                      onEdit={() => {
                        setRowToEdit(row);
                        setIsEditRowDialogOpen(true);
                      }}
                    />
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {rowToCopy?.dayOrDate && isOfTypeDay(rowToCopy?.dayOrDate) ? (
        <ApplyOnMultipleDaysDialog<T>
          day={rowToCopy?.dayOrDate}
          timeRanges={rowToCopy?.timeRanges ?? []}
          open={rowToCopy != null}
          onSave={handleCopyOpeningHours}
          onClose={() => setRowToCopy(undefined)}
        />
      ) : rowToCopy?.dayOrDate && !isOfTypeDay(rowToCopy?.dayOrDate) ? (
        <CopyExceptionDialog
          date={rowToCopy.dayOrDate}
          timeRanges={rowToCopy?.timeRanges}
          open={rowToCopy != null}
          onSave={handleCopyOpeningHours}
          onClose={() => setRowToCopy(undefined)}
        />
      ) : null}
      <AddEditExceptionDialog
        date={rowToEdit?.dayOrDate}
        openingHours={rowToEdit?.timeRanges}
        isOpened={rowToEdit?.timeRanges.length != undefined && rowToEdit?.timeRanges.length > 0}
        open={isEditRowDialogOpen}
        onSave={(date, timeRanges) => {
          const newOpeningHours = _.cloneDeep(openingHours);
          if (date !== rowToEdit?.dayOrDate) {
            delete newOpeningHours[rowToEdit?.dayOrDate];
          }

          onChange({ ...newOpeningHours, [date]: timeRanges });
        }}
        onClose={() => {
          setIsEditRowDialogOpen(false);
          setRowToEdit(undefined);
        }}
        defaultData={emptyExtensionData}
        ExtensionComponent={ExtensionComponent}
      />
    </Box>
  );
};
